import React, { useState, useEffect } from "react";
import { Card, Upload, Button, message, Progress, Space, Typography, Alert, Statistic, Row, Col, Switch, Radio, Checkbox, Divider } from "antd";
import { InboxOutlined, UploadOutlined, CheckCircleOutlined, SyncOutlined, SettingOutlined, FileExcelOutlined } from "@ant-design/icons";
import { 
  uploadCustomerFile, 
  getUploadStatus, 
  previewFileColumns, 
  getAvailableFields,
  uploadWithMapping 
} from "../../../api/customer";
import FieldMappingModal from "../../../components/FieldMappingModal";
import UploadedFilesList from "../../../components/UploadedFilesList";
import MailingMetadataModal from "../../../components/MailingMetadataModal";

const { Dragger } = Upload;
const { Title, Text } = Typography;
const TotalCustomer = () => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  
  // Field mapping states
  const [useCustomMapping, setUseCustomMapping] = useState(true);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [availableFields, setAvailableFields] = useState(null);
  const [fieldMapping, setFieldMapping] = useState(null);
  
  // Mailing metadata states
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [uniqueKeyCodes, setUniqueKeyCodes] = useState([]);

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning("Please select a file to upload");
      return;
    }

    // If custom mapping is enabled, show mapping modal first
    if (useCustomMapping) {
      try {
        message.loading("Analyzing file structure...", 0);
        
        // Fetch available fields if not already loaded
        if (!availableFields) {
          const fields = await getAvailableFields();
          setAvailableFields(fields);
        }
        
        // Preview file columns with position-based mode by default
        const preview = await previewFileColumns(fileList[0], {
          mapping_mode: 'position',
          has_header: false
        });
        setFilePreview(preview);
        
        message.destroy();
        setShowMappingModal(true);
      } catch (error) {
        message.destroy();
        message.error("Failed to analyze file: " + (error.response?.data?.error || error.message));
      }
      return;
    }

    // Standard upload without mapping
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      fileList.forEach((file) => {
        formData.append("file", file);
      });

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await uploadCustomerFile(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.task_id) {
        message.success("File uploaded successfully! Processing started...");
        setTaskId(response.task_id);
        setPolling(true);
        setFileList([]);
        setUploadProgress(0);
      } else {
        message.success(response.message || "File uploaded successfully!");
        setFileList([]);
        setTimeout(() => {
          setUploadProgress(0);
        }, 2000);
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to upload file");
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleMappingConfirm = async (mapping) => {
    setShowMappingModal(false);
    setFieldMapping(mapping);
    
    // Extract unique key codes from file preview if key_code was mapped
    if (mapping.key_code && filePreview && filePreview.sample_data) {
      const keyCodes = new Set();
      filePreview.sample_data.forEach(row => {
        const keyCodeValue = row[Object.keys(mapping).find(k => mapping[k] === 'key_code')];
        if (keyCodeValue) {
          keyCodes.add(keyCodeValue);
        }
      });
      setUniqueKeyCodes(Array.from(keyCodes));
    }
    
    // Show metadata modal to collect mailing information
    setShowMetadataModal(true);
  };

  const handleMetadataConfirm = async (metadata) => {
    setShowMetadataModal(false);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Upload with mapping and metadata
      // Wrap mailing metadata in the expected structure
      const uploadMetadata = {
        mailing_metadata: metadata
      };
      const response = await uploadWithMapping(fileList[0], fieldMapping, uploadMetadata);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.task_id) {
        message.success("File uploaded with custom mapping! Processing started...");
        setTaskId(response.task_id);
        setPolling(true);
        setFileList([]);
        setUploadProgress(0);
        setFieldMapping(null);
        setUniqueKeyCodes([]);
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to upload file");
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  // Poll for task status
  useEffect(() => {
    if (!taskId || !polling) return;

    const pollStatus = async () => {
      try {
        const status = await getUploadStatus(taskId);
        setTaskStatus(status);

        // Stop polling if completed or failed
        if (status.status === 'completed') {
          setPolling(false);
          message.success('File processing completed successfully!');
        } else if (status.status === 'failed') {
          setPolling(false);
          message.error(status.error || 'File processing failed');
        }
      } catch (error) {
        console.error('Error fetching status:', error);
      }
    };

    // Poll immediately
    pollStatus();

    // Then poll every 2 seconds
    const interval = setInterval(pollStatus, 2000);

    return () => clearInterval(interval);
  }, [taskId, polling]);

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setFileList([...fileList, file]);
      return false; // Prevent automatic upload
    },
    fileList,
    multiple: false,
    accept: ".seq,.csv,.tsv,.xlsx,.xls",
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row">
          <div className="col-md-12">
            <Card>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div>
                  <Title level={3}>Upload Customer File</Title>
                  <Text type="secondary">
                    Upload a customer data file for processing. Supports .seq, .csv, .tsv, .xlsx, and .xls files.
                  </Text>
                </div>

                <Dragger {...uploadProps} disabled={uploading}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                  <p className="ant-upload-hint">
                    Support for single file upload. The file will be processed in the background.
                  </p>
                </Dragger>

                {uploadProgress > 0 && (
                  <Progress
                    percent={uploadProgress}
                    status={uploadProgress === 100 ? "success" : "active"}
                  />
                )}

                <Button
                  type="primary"
                  onClick={handleUpload}
                  disabled={fileList.length === 0}
                  loading={uploading}
                  icon={<UploadOutlined />}
                  size="large"
                >
                  {uploading ? "Uploading..." : "Start Upload"}
                </Button>

                {/* Processing Status */}
                {taskStatus && polling && (
                  <Card 
                    title={
                      <Space>
                        <SyncOutlined spin />
                        <span>Processing: {taskStatus.file_name}</span>
                      </Space>
                    }
                    size="small"
                  >
                    <Space direction="vertical" style={{ width: "100%" }} size="middle">
                      <Alert
                        message={taskStatus.message}
                        description={taskStatus.current_step}
                        type="info"
                        showIcon
                      />

                      {taskStatus.progress && (
                        <>
                          <Progress
                            percent={taskStatus.progress.percentage}
                            status="active"
                            strokeColor={{
                              '0%': '#108ee9',
                              '100%': '#87d068',
                            }}
                          />
                          <div>
                            <Text type="secondary">
                              {taskStatus.progress.processed_records} / {taskStatus.progress.total_records} records processed
                              {taskStatus.progress.total_batches > 0 && (
                                <> (Batch {taskStatus.progress.current_batch}/{taskStatus.progress.total_batches})</>
                              )}
                            </Text>
                          </div>
                          {taskStatus.timing && (
                            <div style={{ marginTop: '8px' }}>
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    ⏱️ Elapsed: <strong>{taskStatus.timing.elapsed_formatted}</strong>
                                  </Text>
                                </Col>
                                <Col span={12}>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    ⏳ Remaining: <strong>{taskStatus.timing.estimated_remaining_formatted}</strong>
                                  </Text>
                                </Col>
                              </Row>
                            </div>
                          )}
                        </>
                      )}

                      {taskStatus.results && (taskStatus.results.matched_to_existing > 0 || taskStatus.results.new_unique_persons > 0) && (
                        <Row gutter={16}>
                          <Col span={12}>
                            <Statistic
                              title="Matched to Existing"
                              value={taskStatus.results.matched_to_existing}
                              valueStyle={{ color: '#3f8600' }}
                            />
                          </Col>
                          <Col span={12}>
                            <Statistic
                              title="New Unique Persons"
                              value={taskStatus.results.new_unique_persons}
                              valueStyle={{ color: '#1890ff' }}
                            />
                          </Col>
                        </Row>
                      )}
                    </Space>
                  </Card>
                )}

                {/* Completed Status */}
                {taskStatus && !polling && taskStatus.status === 'completed' && (
                  <Alert
                    message="Processing Completed"
                    description={
                      <Space direction="vertical">
                        <Text>File: {taskStatus.file_name}</Text>
                        <Text>Total Records: {taskStatus.progress?.total_records || 0}</Text>
                        <Text>Matched: {taskStatus.results?.matched_to_existing || 0}</Text>
                        <Text>New: {taskStatus.results?.new_unique_persons || 0}</Text>
                        {taskStatus.timing && (
                          <Text>⏱️ Processing Time: <strong>{taskStatus.timing.elapsed_formatted}</strong></Text>
                        )}
                      </Space>
                    }
                    type="success"
                    icon={<CheckCircleOutlined />}
                    showIcon
                    closable
                    onClose={() => {
                      setTaskStatus(null);
                      setTaskId(null);
                    }}
                  />
                )}
              </Space>
            </Card>
          </div>
 
        </div>
      </div>

      {/* Field Mapping Modal */}
      {filePreview && (
        <FieldMappingModal
          visible={showMappingModal}
          fileColumns={filePreview.columns || []}
          sampleData={filePreview.sample_data || []}
          availableFields={availableFields || {}}
          suggestedMapping={filePreview.suggested_mapping || {}}
          filePreviewId={filePreview.temp_file_id || ''}
          originalFile={fileList[0]}
          onConfirm={handleMappingConfirm}
          onCancel={() => setShowMappingModal(false)}
        />
      )}

      {/* Mailing Metadata Modal */}
      <MailingMetadataModal
        visible={showMetadataModal}
        keyCodes={uniqueKeyCodes}
        onConfirm={handleMetadataConfirm}
        onCancel={() => setShowMetadataModal(false)}
      />

      {/* Upload History */}
      <div style={{ marginTop: 32 }}>
        <UploadedFilesList />
      </div>
    </div>
  );
};

export default TotalCustomer;
